import { createTheme, ThemeProvider } from '@mui/material';
import { Suspense, useEffect, useMemo } from 'react';
import { getThemePrimaryForMode } from './config/themePalette';
import { AppCheckBoxIcon, AppCheckBoxOutlineBlankIcon, AppIndeterminateCheckBoxIcon } from './theme/muiCheckboxIcons';
import classNames from 'classnames';
import { Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { RouteFallback } from './components/Loader';
import AppMainShell, { isWorkbenchShellPath } from './components/common/AppMainShell';
import { MobileNavbarChromeProvider } from './components/common/mobileNavbarChromeContext';
import { MobileIslandDock, MobileIslandDockProvider } from './components/mobile-island-dock';
import { useBreakpoints } from './hooks/useBreakpoints';
import { useHomeChromeDocumentClass } from './hooks/useHomeChromeDocumentClass';
import NotificationProvider from './providers/NotificationProvider';
import ProductProvider from './providers/ProductProvider';

import { socket } from './socket';

import OrderProvider from './providers/OrderProvider';
import './utils/initFA';
import StatisticsProvider from './providers/StatisticsProvider';
import SearchProvider from './providers/SearchProvider';

const App = () => {
	const { pathname } = useLocation();
	const { breakpoints } = useBreakpoints();
	useHomeChromeDocumentClass();
	const isMobileApp = breakpoints.down('md');
	const isDashboardHomeMobile = isMobileApp && pathname === '/dashboard';
	const isWorkbenchPage = isWorkbenchShellPath(pathname);

	const { theme } = useSelector((state) => state.config);

	const mode = theme === 'auto' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : theme === 'light' ? 'light' : 'dark';

	const brandPrimary = getThemePrimaryForMode(mode === 'light');

	const tableTheme = useMemo(
		() =>
			createTheme({
				palette: {
					mode: mode,
					primary: {
						main: brandPrimary,
					},
					info: {
						main: 'rgb(255,122,0)',
					},
					background: {
						default: theme === 'auto' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? '#0b1727' : '#fff') : theme === 'light' ? '#fff' : '#0b1727',
					},
				},
				typography: {
					button: {
						textTransform: 'none',
						fontSize: '1.2rem',
					},
				},
				components: {
					MuiCheckbox: {
						defaultProps: {
							color: 'primary',
							icon: <AppCheckBoxOutlineBlankIcon />,
							checkedIcon: <AppCheckBoxIcon />,
							indeterminateIcon: <AppIndeterminateCheckBoxIcon />,
						},
						styleOverrides: {
							root: {
								'& .MuiSvgIcon-root': {
									fontSize: '1.5rem',
								},
							},
							sizeSmall: {
								'& .MuiSvgIcon-root': {
									fontSize: '1.25rem',
								},
							},
						},
					},
					MuiTooltip: {
						styleOverrides: {
							tooltip: {
								fontSize: '1.1rem',
							},
						},
					},
					MuiSwitch: {
						styleOverrides: {
							thumb: {
								color: 'pink',
							},
						},
					},
				},
			}),
		[brandPrimary, mode, theme],
	);

	useEffect(() => {
		socket.connect();
	}, []);

	return (
		<ThemeProvider theme={tableTheme}>
			<ProductProvider>
				<NotificationProvider>
					<OrderProvider>
						<StatisticsProvider>
							<SearchProvider>
								<MobileNavbarChromeProvider>
									<MobileIslandDockProvider>
										{/* Falcon MainLayout：NavbarTop 在 .content 滚动流内 sticky，不是 main 外 fixed */}
										<main
											className={classNames({
												'dashboard-home-mobile': isDashboardHomeMobile,
												'pb-3': !isDashboardHomeMobile,
											})}
										>
											<Container
												fluid
												className={classNames({
													'dashboard-container--home-mobile': isDashboardHomeMobile,
													'app-page-shell--fluid': isWorkbenchPage,
												})}
											>
												<div className='content app-main-content'>
													<AppMainShell>
														<Suspense fallback={<RouteFallback />}>
															<Outlet />
														</Suspense>
													</AppMainShell>
												</div>
											</Container>
										</main>
										{isMobileApp ? <MobileIslandDock /> : null}
									</MobileIslandDockProvider>
								</MobileNavbarChromeProvider>
							</SearchProvider>
						</StatisticsProvider>
					</OrderProvider>
				</NotificationProvider>
			</ProductProvider>
		</ThemeProvider>
	);
};

export default App;
